import { useEffect, useRef, useState } from "react";
import { post } from "app/popleads/networking/RequestService";

// A stored verification the backend would not repeat; anything else (none, unknown, error) is worth a check.
const SETTLED = ["ok", "invalid", "disposable", "catch_all"];

const normalize = (email) =>
    String(email || "")
        .trim()
        .toLowerCase();

// Nothing to verify once the user has saved one of the suggestions to the profile (no credits spent).
const needsVerification = (suggestions, ownEmails) =>
    !suggestions.some((s) => ownEmails.includes(normalize(s.email))) &&
    suggestions.some((s) => !s.ownedByOtherProfile && !SETTLED.includes(s.verification?.status));

export const EMPTY_SUGGESTIONS = {
    suggestions: [],
    reliability: null,
    emailPattern: null,
    verifiable: false,
    loading: false,
    verifying: false,
};
const EMPTY = EMPTY_SUGGESTIONS;

/**
 * Reads a getEmailSuggestions-style response. verifiable: the backend verifies an account's suggestions only when
 * its domain has enough known addresses (EmailVerificationDBOp.MIN_DOMAIN_ADDRESSES); a backend without the field
 * counts as verifiable.
 */
export const readSuggestions = (res) => {
    if (res.data?.stat !== "ok") throw new Error(res.data?.text || "email suggestions failed");
    return {
        suggestions: res.data?.suggestions || [],
        reliability: res.data?.emailPattern?.reliability || null,
        emailPattern: res.data?.emailPattern || null,
        verifiable: res.data?.verifiable !== false,
    };
};

/**
 * Corporate email addresses suggested from the email pattern of the profile's account
 * (backend fn getEmailSuggestions), shown at once. When some of them have no verification yet and the account is
 * verifiable, verifyEmailSuggestions runs in the background and the rows are refreshed with its result.
 * Suggestions are optional: on any error the list is empty, and a failed verification keeps the list.
 *
 * @param {number} profileId
 * @param {Array<string>} [profileEmails] the profile's own email and email2; a saved suggestion is not verified
 * @returns {{ suggestions: Array<{email: string, pattern: string, rank: number, ownedByOtherProfile: boolean,
 *   matchesProfileEmail: boolean, verification: ?{status: string, verifiedTime: number}}>,
 *   reliability: string|null, emailPattern: ?Object, verifiable: boolean, loading: boolean, verifying: boolean }}
 */
const useEmailSuggestions = (profileId, profileEmails = []) => {
    const [state, setState] = useState(EMPTY);
    const ownEmails = useRef([]);
    ownEmails.current = profileEmails.map(normalize).filter(Boolean);

    useEffect(() => {
        if (!profileId) return undefined;
        let cancelled = false;
        setState({ ...EMPTY, loading: true });

        const read = readSuggestions;

        post({ fn: "getEmailSuggestions", profileId })
            .then((res) => {
                if (cancelled) return;
                const shown = read(res);
                const verifying = shown.verifiable && needsVerification(shown.suggestions, ownEmails.current);
                setState({ ...shown, loading: false, verifying });
                if (!verifying) return;
                post({ fn: "verifyEmailSuggestions", profileId })
                    .then((verified) => {
                        if (!cancelled) setState({ ...read(verified), loading: false, verifying: false });
                    })
                    .catch(() => {
                        if (!cancelled) setState((prev) => ({ ...prev, verifying: false }));
                    });
            })
            .catch(() => {
                if (!cancelled) setState(EMPTY);
            });
        return () => {
            cancelled = true;
        };
    }, [profileId]);

    return state;
};

export default useEmailSuggestions;
